import { EventKind, IndexTypes, Kind } from "./../features/fsm/types";
import {
  ChildMachineSchema,
  ChildEvents,
  FlowObjects,
  IndexName,
} from "../features/fsm/types";
import Color from "./Color";
import ImageAsset, { PdfAsset } from "./Assets";
import GalleryAsset from "./GalleryAsset";
import Action, {
  ScreenLinkAction,
  FlowLinkAction,
  MailLinkAction,
  LinkAction,
  GalleryLinkAction,
} from "./Action";
import { UploadResponse } from "./Screens";
import { getCurrentDate } from "../features/utils/commonFunctions";
//@ts-ignore
import { Interpreter } from "../features/fsm/xstate";
import { final } from "./finalDecorator";
import * as Sentry from "@sentry/react"

@final
export default class FlowObject implements Kind {
  id: string;
  borderColor: Color;
  rotation: 0 | 1 | 2 | 3;
  galleryAssets: GalleryAsset | null;
  updated: string;
  frame: FrameVal;
  videoAsset: ImageAsset | null;
  zoomScale: number;
  retinaImageAsset: ImageAsset | null;
  pdfAsset: PdfAsset | null;
  type: MixedMediaObjectType;
  action: Action | null;
  originalImageAsset: ImageAsset | null;
  borderWidth: number;
  zoomOffset: {
    height: number;
    width: number;
  };
  audioAsset: ImageAsset | null;
  croppedImageAsset: ImageAsset | null;
  created: string;
  locked: boolean;
  cornerRadius: number;
  /**
   * Hidden from JSON.parse, this informs video/pdf objects to only use previewImage
   */
  __forceUseImage: boolean;
  /**
   * Associated {@link MediaMachine} for this object
   */
  __ref?: Interpreter<
    { mmo: FlowObjects },
    ChildMachineSchema,
    ChildEvents,
    any
  >;

  constructor(
    id: string,
    borderColor: Color,
    rotation: 0 | 1 | 2 | 3,
    galleryAssets: GalleryAsset | null,
    updated: string,
    frame: FrameVal,
    videoAsset: ImageAsset | null,
    zoomScale: number,
    retinaImageAsset: ImageAsset | null,
    pdfAsset: PdfAsset | null,
    type: MixedMediaObjectType,
    action: Action | null,
    originalImageAsset: ImageAsset | null,
    borderWidth: number,
    zoomOffset: {
      height: number;
      width: number;
    },
    audioAsset: ImageAsset | null,
    croppedImageAsset: ImageAsset | null,
    created: string,
    locked: boolean,
    __forceUseImage: boolean,
    cornerRadius: number,
  ) {
    this.id = id;
    this.borderColor = borderColor;
    this.rotation = rotation;
    this.galleryAssets = galleryAssets;
    this.updated = updated;
    this.frame = frame;
    this.videoAsset = videoAsset;
    this.zoomScale = zoomScale;
    this.retinaImageAsset = retinaImageAsset;
    this.pdfAsset = pdfAsset;
    this.type = type;
    this.action = action;
    this.originalImageAsset = originalImageAsset;
    this.borderWidth = borderWidth;
    this.zoomOffset = zoomOffset;
    this.audioAsset = audioAsset;
    this.croppedImageAsset = croppedImageAsset;
    this.created = created;
    this.locked = locked;
    this.__forceUseImage = __forceUseImage;
    this.cornerRadius = cornerRadius;
  }

  eventKind(): EventKind {
    return EventKind.Screen;
  }

  static fromJSON(json: any): FlowObject {
    let flowObj = Object.create(FlowObject.prototype);
    Object.assign(flowObj, json, {
      borderColor: Color.fromJSON(json.borderColor),
      retinaImageAsset: ImageAsset.fromJSON(json.retinaImageAsset),
      pdfAsset: ImageAsset.fromJSON(json.pdfAsset),
      originalImageAsset: ImageAsset.fromJSON(json.originalImageAsset),
      audioAsset: ImageAsset.fromJSON(json.audioAsset),
      croppedImageAsset: ImageAsset.fromJSON(json.croppedImageAsset),
      action: actionFromJSON(json.action),
      frame: parseFrame(json.frame),
      __forceUseImage: false,
      cornerRadius: json.cornerRadius || 0,
    });
    return flowObj;
  }

  static reviver(key: string, value: any): any {
    return key === "" ? this.fromJSON(value) : value;
  }

  /**
   * Updates object to use CDN image instead of local
   *
   * Needs `indexMap` since backend provides no context to where responses should go.
   *
   * @param response [UploadResponse] a single element array with an `ssl_address` and `treemoContentID`
   * @param indexMap hashmap defining which index in upload response maps to fields on this object
   */
  setMMOUrl(response: UploadResponse[], indexMap: Map<IndexName, number>) {
    let index = indexMap.get(IndexTypes.originalImageAsset);
    Sentry.addBreadcrumb({
      category: 'debug',
      message: 'media upload response',
      data: { response, indexMap },
      level: Sentry.Severity.Info,
      type: 'code',
    });
    if (typeof index !== "undefined") {
      let { ssl_address, contentID: treemoContentID } = response[index];
      this.originalImageAsset!.externalURL = {
        __tag: "string",
        value: ssl_address,
      };
      this.originalImageAsset!.treemoContentID = treemoContentID;
      this.originalImageAsset!.updated = getCurrentDate();
    }
    index = indexMap.get(IndexTypes.croppedImageAsset);
    if (typeof index !== "undefined") {
      let { ssl_address, contentID: treemoContentID } = response[index];
      this.croppedImageAsset!.externalURL = {
        __tag: "string",
        value: ssl_address,
      };
      this.croppedImageAsset!.treemoContentID = treemoContentID;
      this.croppedImageAsset!.updated = getCurrentDate();
    }
  }
}

export function actionFromJSON(action: any): Action | null {
  if (action != null) {
    if (action.weblink && action.weblink.startsWith("flowLink://")) {
      return { __tag: "flow", value: FlowLinkAction.fromJSON(action) };
    } else if (action.weblink && action.weblink.startsWith("mailto:")) {
      return { __tag: "email", value: MailLinkAction.fromJSON(action) };
    } else if (action.weblink) {
      return { __tag: "web", value: LinkAction.fromJSON(action) };
    } else if (action.ScreenLinkTransition) {
      return { __tag: "screen", value: ScreenLinkAction.fromJSON(action) };
    } else if (action.action_type === "galleryLink") {
      return { __tag: "gallery", value: GalleryLinkAction.fromJSON(action) };
    }
  }
  return null;
}

export type MixedMediaObjectType =
  | "text"
  | "shape"
  | "pdf"
  | "video"
  | "image"
  | "gallery"
  | "audio"
  | "webview"
  | "none";

export type FrameVal = { x: number; y: number; width: number; height: number };

export function parseFrame(frame: any): FrameVal {
  return {
    x: parseFloat(frame.x),
    y: parseFloat(frame.y),
    width: parseFloat(frame.width),
    height: parseFloat(frame.height),
  };
}

FlowObject.displayName = "FlowObject";
